<template>
  <div>

    <!-- ── Filtros ───────────────────────────────────────────────────────── -->
    <div class="pb-2">
      <v-card elevation="0" outlined style="border-radius: 10px;">
        <v-card-text class="pt-3 pb-2 px-2">
          <v-form @submit.prevent="buscar">
            <v-row align="center" no-gutters>

              <!-- Filial -->
              <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                <SeletorFiliais
                    :filiais="filiaisWolihub"
                    v-model="busca.idfiliais"
                    label="Filial *"
                    :single="true"
                    :clearable="false"
                    :shortcuts="[]"
                    :disabled="carregando"
                />
              </v-col>

              <!-- Período -->
              <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                <SeletorPeriodo
                    :disabled="carregando"
                    v-model="busca.periodo"
                    label="Período *"
                    :shortcuts="['periods', 'weeks', 'months']"
                />
              </v-col>

              <!-- Cliente -->
              <v-col cols="12" sm class="px-1 mb-2 mb-sm-0">
                <SeletorCliente
                    v-model="busca.cliente"
                    label="Cliente"
                    :disabled="carregando"
                />
              </v-col>

              <!-- Nº Pedido -->
              <v-col cols="12" sm="2" class="px-1 mb-2 mb-sm-0">
                <v-text-field
                    v-model="busca.idorcamento"
                    label="Nº Pedido"
                    outlined dense clearable hide-details
                    :disabled="carregando"
                    type="number"
                    prepend-inner-icon="mdi-file-document-outline"
                    class="filtro-field"
                />
              </v-col>

              <!-- Buscar -->
              <v-col cols="12" sm="auto" class="px-1">
                <v-tooltip bottom :disabled="podeBuscar" color="warning">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-btn
                          class="primary elevation-0 d-none d-sm-flex mt-n1"
                          :loading="carregando" :disabled="!podeBuscar"
                          style="border-radius: 8px; height: 40px;"
                          @click="buscar"
                      >
                        <v-icon small left>mdi-magnify</v-icon>
                        Buscar
                      </v-btn>
                    </div>
                  </template>
                  <span>{{ !filialSelecionada ? 'Selecione uma filial.' : 'Selecione o período.' }}</span>
                </v-tooltip>
                <v-btn
                    class="primary elevation-0 d-flex d-sm-none"
                    block :loading="carregando" :disabled="!podeBuscar"
                    style="border-radius: 8px; height: 40px;"
                    @click="buscar"
                >
                  <v-icon small left>mdi-magnify</v-icon>
                  Buscar Pedidos
                </v-btn>
              </v-col>

            </v-row>
          </v-form>

          <v-slide-y-transition>
            <v-alert
                v-if="alertaFilial"
                type="warning" dense outlined class="mt-2 mb-0"
                style="font-size: 12px; border-radius: 8px;"
            >
              Selecione ao menos uma filial para realizar a busca.
            </v-alert>
          </v-slide-y-transition>
        </v-card-text>
      </v-card>
    </div>

    <!-- ── Tabela ─────────────────────────────────────────────────────────── -->
    <div class="pt-2">
      <v-card elevation="0" outlined style="border-radius: 10px; overflow: hidden;">
        <v-progress-linear indeterminate absolute :active="carregando" color="primary" />

        <!-- Cabeçalho -->
        <div class="table-header">

          <!-- Título + timestamp -->
          <div class="table-header-row table-header-row--top">
            <div class="table-header-left">
              <div class="table-header-icon">
                <v-icon size="16" color="white">mdi-receipt-text-outline</v-icon>
              </div>
              <div>
                <div class="table-header-title">Pedidos com E-book</div>
                <div v-if="!pedidos.length" class="table-header-sub">Utilize os filtros acima e clique em Buscar</div>
              </div>
            </div>
            <div class="table-header-actions">
              <button
                  v-if="permissao('wolihub_cliente_excecao')"
                  class="th-toggle-btn d-none d-md-flex"
                  @click="dialogExcecao = true"
              >
                <v-icon size="13" color="rgba(255,255,255,0.8)">mdi-account-cancel-outline</v-icon>
                <span class="th-exc-label">Exceção de Clientes</span>
              </button>
              <div v-if="ultimaBusca" class="table-header-timestamp">
                <v-icon size="12" color="rgba(255,255,255,0.75)" class="mr-1">mdi-clock-check-outline</v-icon>
                <span class="table-header-timestamp-label">Atualizado às</span>
                <span class="table-header-timestamp-hora">{{ horaUltimaBusca }}</span>
              </div>
              <v-tooltip v-if="pedidos.length" bottom color="#023449">
                <template v-slot:activator="{ on, attrs }">
                  <button
                      class="th-toggle-btn"
                      :class="{ 'th-toggle-btn--ativo': filtrosVisiveis }"
                      v-bind="attrs" v-on="on"
                      @click="filtrosVisiveis = !filtrosVisiveis"
                  >
                    <v-icon size="14" color="rgba(255,255,255,0.8)">mdi-filter-outline</v-icon>
                    <v-icon size="12" color="rgba(255,255,255,0.6)" style="margin-left:2px;">
                      {{ filtrosVisiveis ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                  </button>
                </template>
                <span>{{ filtrosVisiveis ? 'Ocultar filtros' : 'Exibir filtros' }}</span>
              </v-tooltip>
            </div>
          </div>

        </div>

        <!-- ── Barra de filtros ─────────────────────────────────────────── -->
        <v-expand-transition>
          <div v-if="pedidos.length && filtrosVisiveis" class="filtro-toolbar">

            <!-- Status -->
            <div class="seg-group">
              <div class="seg-label-row">
              <span class="seg-label">
                <v-icon size="10" color="#94a3b8">mdi-receipt-text-outline</v-icon>
                <span>Quantidade e valores por pedido de venda</span>
              </span>
              </div>
              <div class="seg-track">
                <button
                    v-for="opt in statusFiltroOpcoes"
                    :key="String(opt.value)"
                    class="seg-btn"
                    :class="{ 'seg-btn--ativo': filtroStatus === opt.value }"
                    :style="filtroStatus === opt.value ? { '--seg-cor': opt.hex } : {}"
                    @click="filtroStatus = opt.value"
                >
                  <div class="seg-btn-top">
                    <span class="seg-btn-label">{{ opt.label }}</span>
                    <span class="seg-btn-count">{{ contagens[opt.chave] }}</span>
                  </div>
                  <span class="seg-btn-div" />
                  <span class="seg-btn-valor">{{ valoresStatus[opt.chave] | formataDinheiro }}</span>
                </button>
              </div>
            </div>

            <div class="filtro-toolbar-sep" />

            <!-- Combo -->
            <div class="seg-group" :class="{ 'seg-group--filtrado': filtroStatus }">
              <div class="seg-label-row">
              <span class="seg-label">
                <v-icon size="10" color="#94a3b8">mdi-book-open-outline</v-icon>
                <span>Quantidade e valores por e-book</span>
              </span>
                <span
                    class="seg-dep-badge"
                    :class="{ 'seg-dep-badge--visivel': filtroStatus }"
                    :style="{ '--dep-cor': filtroStatus ? statusFiltroOpcoes.find(o => o.value === filtroStatus).hex : '#94a3b8' }"
                >
                <v-icon size="9" :color="filtroStatus ? statusFiltroOpcoes.find(o => o.value === filtroStatus).hex : '#94a3b8'">mdi-subdirectory-arrow-right</v-icon>
                {{ filtroStatus ? 'dos ' + statusFiltroOpcoes.find(o => o.value === filtroStatus).label.toLowerCase() : 'placeholder' }}
              </span>
              </div>
              <div class="seg-track">
                <button
                    v-for="combo in combosOpcoes"
                    :key="String(combo.value)"
                    class="seg-btn"
                    :class="{ 'seg-btn--ativo': filtroCombo === combo.value }"
                    :style="filtroCombo === combo.value ? { '--seg-cor': combo.hex } : {}"
                    @click="filtroCombo = combo.value"
                >
                  <div class="seg-btn-top">
                    <span class="seg-btn-label">{{ combo.label }}</span>
                    <span class="seg-btn-count">{{ contagensCombo[combo.value] }}</span>
                  </div>
                  <span class="seg-btn-div" />
                  <span class="seg-btn-valor">{{ valoresCombo[combo.value] | formataDinheiro }}</span>
                </button>
              </div>
            </div>

          </div>
        </v-expand-transition>

        <!-- ── Desktop ──────────────────────────────────────────────────── -->
        <div class="d-none d-md-block">
          <v-simple-table class="pedidos-table">
            <colgroup>
              <col style="width: 100px;">  <!-- Pedido   -->
              <col style="width: 140px;">  <!-- Filial   -->
              <col style="min-width: 170px;"> <!-- Cliente (ocupa o restante) -->
              <col style="width: 104px;">  <!-- Combo    -->
              <col style="width: 110px;">  <!-- Qtde     -->
              <col style="width: 120px;">  <!-- Vl. Ebook -->
              <col style="width: 120px;">  <!-- Vl. Total -->
              <col style="width: 104px;">  <!-- Status   -->
              <col style="width: 168px;">  <!-- Ações    -->
            </colgroup>
            <thead>
            <tr class="thead-row">
              <th class="text-caption font-weight-bold thead-th">Pedido</th>
              <th class="text-caption font-weight-bold thead-th">Filial</th>
              <th class="text-caption font-weight-bold thead-th">Cliente</th>
              <!--                <th class="text-caption font-weight-bold thead-th">Vendedor</th>-->
              <th class="text-caption font-weight-bold thead-th text-center">Combo</th>
              <th class="text-caption font-weight-bold thead-th text-center">Qtde E-book</th>
              <th class="text-caption font-weight-bold thead-th text-right">Valor E-book</th>
              <th class="text-caption font-weight-bold thead-th text-right">Valor Total</th>
              <th class="text-caption font-weight-bold thead-th text-center">Status</th>
              <th class="text-caption font-weight-bold thead-th text-center">Ações</th>
            </tr>
            </thead>

            <!-- Skeleton -->
            <tbody v-if="carregando">
            <tr v-for="n in 5" :key="`sk-${n}`" :style="{ opacity: 1 - n * 0.15 }">
              <td><div class="sk sk-text" style="width:64px;"></div></td>
              <td><div class="sk sk-text" style="width:110px;"></div></td>
              <td><div class="sk sk-text" style="width:160px;"></div></td>
              <td><div class="sk sk-text" style="width:90px;"></div></td>
              <td><div class="sk sk-chip" style="width:66px;margin-left:auto;"></div></td>
              <td><div class="sk sk-text" style="width:68px;margin-left:auto;"></div></td>
              <td><div class="sk sk-text" style="width:78px;margin-left:auto;"></div></td>
              <td><div class="sk sk-chip" style="width:76px;margin:auto;"></div></td>
              <td><div class="sk sk-chip" style="width:80px;margin:auto;"></div></td>
            </tr>
            </tbody>
            <tbody v-else-if="pedidosFiltrados.length">
            <template v-for="(item, i) in pedidosPaginados">
              <tr
                  :key="`r-${i}`"
                  class="data-row"
                  :class="{ 'data-row--inativo': item.flagebook === 'F' }"
              >

                <!-- Pedido + data -->
                <td class="pedido-cell">
                  <div class="pedido-badge">#{{ item.idorcamento }}</div>
                  <span class="pedido-data">
                    <v-icon size="9" color="#b0c4d4" style="margin-right:2px; vertical-align:middle;">mdi-calendar-outline</v-icon>
                    {{ parseDtPedido(item.dtpedido).data }}
                  </span>
                  <span class="pedido-hora">
                    <v-icon size="9" color="#b0c4d4" style="margin-right:2px; vertical-align:middle;">mdi-clock-outline</v-icon>
                    {{ parseDtPedido(item.dtpedido).hora }}
                  </span>
                </td>

                <!-- Filial -->
                <td class="text-caption" style="color:#5a6a7a;">
                  {{ item.empresa || item.idempresa }}
                </td>

                <!-- Cliente -->
                <td class="text-caption">
                  {{ item.nomecliente }}
                  <span class="id-sec">({{ item.idclifor }})</span>
                </td>

                <!-- Vendedor -->
                <!--                <td class="text-caption" style="color:#5a6a7a;">{{ item.nomevendedor }}</td>-->

                <!-- Combo -->
                <td class="text-center">
                  <v-chip x-small :color="corCombo(item.combo)" dark style="font-weight:600;">
                    {{ item.combo }}
                  </v-chip>
                </td>

                <!-- Qtde e-book -->
                <td class="text-center text-caption font-weight-bold" style="color:#023449;">
                  {{ item.qtdebook }}
                </td>

                <!-- Valor e-book -->
                <td class="text-right text-caption font-weight-bold" style="color:#023449;">
                  {{ item.valorebook | formataDinheiro }}
                </td>

                <!-- Valor total -->
                <td class="text-right text-caption" style="color:#5a6a7a;">
                  {{ item.vlrtotal | formataDinheiro }}
                </td>

                <!-- Status simplificado -->
                <td class="text-center">
                  <v-chip
                      :color="statusPedido(item).color"
                      x-small dark
                      style="font-weight:600;"
                  >
                    <v-icon x-small left>{{ statusPedido(item).icon }}</v-icon>
                    {{ statusPedido(item).label }}
                  </v-chip>
                </td>

                <!-- Ações: ícones + tooltip (desktop) -->
                <td class="text-center">
                  <div class="acoes-wrap">

                    <!-- Remover e-book (oculto apenas se sem permissão) -->
                    <v-tooltip v-if="permissao('wolihub_remover_ebook')" bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          <v-btn
                              icon x-small color="error"
                              :disabled="item.flagebook === 'F' || item.flagefetivado === 'T'"
                              @click="abrirRemocao(item)"
                          >
                            <v-icon small>mdi-book-remove</v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ tooltipRemover(item) }}</span>
                    </v-tooltip>

                    <!-- Autorizar devolução (oculto apenas se sem permissão) -->
                    <v-tooltip v-if="permissao('wolihub_autorizar_devolucao')" bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          <v-btn
                              icon x-small
                              :color="item.flagautorizado === 'T' ? 'teal' : 'primary'"
                              :disabled="!podeAutorizar(item)"
                              @click="abrirAutorizacao(item)"
                          >
                            <v-icon small>
                              {{ item.flagautorizado === 'T' ? 'mdi-shield-check-outline' : 'mdi-book-check-outline' }}
                            </v-icon>
                          </v-btn>
                        </span>
                      </template>
                      <span>{{ tooltipAutorizar(item) }}</span>
                    </v-tooltip>

                    <!-- Ver detalhes -->
                    <v-tooltip v-if="permissao('wolihub_ver_detalhes')" bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small color="indigo" v-bind="attrs" v-on="on" @click="abrirInfo(item)">
                          <v-icon small>mdi-information-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver Detalhes</span>
                    </v-tooltip>

                    <!-- Comprovante de venda -->
                    <v-tooltip v-if="permissao('wolihub_imprimir_comprovante')" bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small color="primary" v-bind="attrs" v-on="on" :loading="gerandoPDF[`${item.idempresa}_${item.idorcamento}`]" @click="iniciarPDFVenda(item)">
                          <v-icon small>mdi-printer-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Comprovante de Venda</span>
                    </v-tooltip>

                    <!-- Expandir produtos (oculto se sem permissão) -->
                    <v-tooltip v-if="permissao('wolihub_lista_produto')" bottom color="primary">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon x-small color="primary" v-bind="attrs" v-on="on" @click="toggleExpandir(item)">
                          <v-icon small>{{ estaExpandido(item) ? 'mdi-package-variant' : 'mdi-package-variant-closed' }}</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ estaExpandido(item) ? 'Recolher produtos' : 'Ver produtos do pedido' }}</span>
                    </v-tooltip>

                  </div>
                </td>
              </tr>

              <!-- ── Linha expandida: produtos do pedido ───────────────── -->
              <tr
                  v-if="permissao('wolihub_lista_produto') && estaExpandido(item)"
                  :key="`exp-${i}`"
                  class="expanded-row"
              >
                <td colspan="9" class="pa-0">
                  <PedidoProdutosExpandido
                      :produtos="produtosDoItem(item)"
                      :totais="totaisProdutos(item)"
                      :carregando="estaCarregandoProdutos(item)"
                      :tem-devolucao="temDevolucao(item)"
                  />
                </td>
              </tr>
            </template>
            </tbody>

            <!-- Footer com totalizadores -->
            <tfoot v-if="pedidosFiltrados.length">
            <tr class="pedidos-tfoot-row">
              <td class="pedidos-tfoot-td" colspan="4">
                <div class="d-flex align-center" style="gap:12px;">
                    <span class="pedidos-tfoot-label">
                      {{ pedidosFiltrados.length }} pedido{{ pedidosFiltrados.length !== 1 ? 's' : '' }}
                    </span>
                  <span v-if="totalPaginas > 1" class="pedidos-tfoot-pagina">
                      página {{ paginaAtual }} de {{ totalPaginas }}
                    </span>
                </div>
              </td>
              <td class="pedidos-tfoot-td text-center">
                <span class="pedidos-tfoot-val">{{ totaisPedidos.qtdEbooks }}</span>
              </td>
              <td class="pedidos-tfoot-td text-right">
                <span class="pedidos-tfoot-val">{{ totaisPedidos.valorEbooks | formataDinheiro }}</span>
              </td>
              <td class="pedidos-tfoot-td text-right">
                <span class="pedidos-tfoot-val pedidos-tfoot-val--total">{{ totaisPedidos.valorTotal | formataDinheiro }}</span>
              </td>
              <td class="pedidos-tfoot-td" colspan="2">
                <div v-if="totalPaginas > 1" class="pedidos-paginacao">
                  <button
                      class="pag-btn"
                      :disabled="paginaAtual === 1"
                      @click="paginaAtual = 1"
                  ><v-icon size="14">mdi-page-first</v-icon></button>
                  <button
                      class="pag-btn"
                      :disabled="paginaAtual === 1"
                      @click="paginaAtual--"
                  ><v-icon size="14">mdi-chevron-left</v-icon></button>
                  <button
                      class="pag-btn"
                      :disabled="paginaAtual === totalPaginas"
                      @click="paginaAtual++"
                  ><v-icon size="14">mdi-chevron-right</v-icon></button>
                  <button
                      class="pag-btn"
                      :disabled="paginaAtual === totalPaginas"
                      @click="paginaAtual = totalPaginas"
                  ><v-icon size="14">mdi-page-last</v-icon></button>
                </div>
              </td>
            </tr>
            </tfoot>

            <tbody v-if="!carregando && !pedidosFiltrados.length">
            <tr>
              <td colspan="9" class="text-center pa-10">
                <div class="d-flex flex-column align-center">
                  <v-icon size="44" color="grey lighten-2" class="mb-3">mdi-receipt-text-outline</v-icon>
                  <span class="grey--text text--lighten-1 body-2">Nenhum pedido encontrado.</span>
                  <span class="grey--text text--lighten-2 caption mt-1">
                      {{ pedidos.length ? 'Ajuste o filtro de status acima.' : 'Utilize os filtros e clique em Buscar.' }}
                    </span>
                </div>
              </td>
            </tr>
            </tbody>
          </v-simple-table>
        </div>

        <!-- ── Mobile ───────────────────────────────────────────────────── -->
        <div class="d-block d-md-none">

          <!-- Skeleton -->
          <template v-if="carregando">
            <div v-for="n in 4" :key="`msk-${n}`" class="mobile-card" :style="{ opacity: 1 - n * 0.2 }">
              <div class="mobile-card-header">
                <div>
                  <div class="sk sk-text" style="width:80px; margin-bottom:5px;"></div>
                  <div class="sk sk-text" style="width:140px;"></div>
                </div>
                <div style="display:flex; gap:6px;">
                  <div class="sk sk-chip" style="width:56px;"></div>
                  <div class="sk sk-chip" style="width:48px;"></div>
                </div>
              </div>
              <div class="mobile-card-body">
                <div>
                  <div class="sk sk-text" style="width:40px; margin-bottom:5px;"></div>
                  <div class="sk sk-text" style="width:110px;"></div>
                </div>
                <div>
                  <div class="sk sk-text" style="width:52px; margin-bottom:5px;"></div>
                  <div class="sk sk-text" style="width:76px;"></div>
                </div>
                <div>
                  <div class="sk sk-text" style="width:60px; margin-bottom:5px;"></div>
                  <div class="sk sk-text" style="width:64px;"></div>
                </div>
                <div>
                  <div class="sk sk-text" style="width:50px; margin-bottom:5px;"></div>
                  <div class="sk sk-text" style="width:70px;"></div>
                </div>
              </div>
            </div>
          </template>

          <!-- Cards -->
          <template v-else-if="pedidosFiltrados.length">
            <PedidoCardMobile
                v-for="(item, i) in pedidosPaginados"
                :key="`mc-${i}`"
                :item="item"
                :status-pedido="statusPedido(item)"
                :cor-combo="corCombo(item.combo)"
                :expandido="estaExpandido(item)"
                :carregando-produtos="estaCarregandoProdutos(item)"
                :produtos="produtosDoItem(item)"
                :gerando-p-d-f="!!gerandoPDF[`${item.idempresa}_${item.idorcamento}`]"
                :pode-autorizar="podeAutorizar(item)"
                :parse-dt-pedido="parseDtPedido"
                @remover="abrirRemocao"
                @autorizar="abrirAutorizacao"
                @detalhes="abrirInfo"
                @expandir="toggleExpandir"
                @comprovante="iniciarPDFVenda"
            />
          </template>

          <!-- Paginação mobile -->
          <div v-if="pedidosFiltrados.length && totalPaginas > 1" class="mobile-paginacao">
            <button class="pag-btn" :disabled="paginaAtual === 1" @click="paginaAtual = 1">
              <v-icon size="16">mdi-page-first</v-icon>
            </button>
            <button class="pag-btn" :disabled="paginaAtual === 1" @click="paginaAtual--">
              <v-icon size="16">mdi-chevron-left</v-icon>
            </button>
            <span class="mobile-paginacao-label">{{ paginaAtual }} / {{ totalPaginas }}</span>
            <button class="pag-btn" :disabled="paginaAtual === totalPaginas" @click="paginaAtual++">
              <v-icon size="16">mdi-chevron-right</v-icon>
            </button>
            <button class="pag-btn" :disabled="paginaAtual === totalPaginas" @click="paginaAtual = totalPaginas">
              <v-icon size="16">mdi-page-last</v-icon>
            </button>
          </div>

          <!-- Vazio mobile -->
          <div v-else-if="!pedidosFiltrados.length" class="mobile-empty">
            <v-icon size="44" color="grey lighten-2" class="mb-3">mdi-receipt-text-outline</v-icon>
            <span class="grey--text text--lighten-1 body-2">
              {{ pedidos.length ? 'Nenhum pedido neste filtro.' : 'Nenhum pedido encontrado.' }}
            </span>
          </div>
        </div>
      </v-card>
    </div>

    <!-- ── Dialog: Exceção de Clientes ─────────────────────────────────────── -->
    <DialogExcecaoClientes v-model="dialogExcecao" />

    <!-- ── Dialog: Remover E-book ─────────────────────────────────────────── -->
    <DialogConfirmaRemocao
        v-model="dialogRemocao"
        :item="itemSelecionado"
        :salvando="salvando"
        @confirmar="confirmarRemocao"
    />

    <!-- ── Dialog: Autorizar Devolução ───────────────────────────────────── -->
    <DialogAutorizaDevolucao
        v-model="dialogAutorizacao"
        :item="itemSelecionado"
        :salvando="salvando"
        @confirmar="confirmarAutorizacao"
    />

    <!-- ── Dialog: Detalhes / Logs ────────────────────────────────────────── -->
    <v-dialog v-model="dialogInfo" max-width="480">
      <v-card style="border-radius: 12px; overflow: hidden;">

        <div class="info-header">
          <div class="info-header-icon">
            <v-icon size="16" color="white">mdi-information-outline</v-icon>
          </div>
          <div>
            <div class="info-header-title">Detalhes do Pedido</div>
            <div class="info-header-sub" v-if="itemInfo">
              #{{ itemInfo.idorcamento }} · {{ itemInfo.empresa || itemInfo.idempresa }}
            </div>
          </div>
        </div>

        <v-card-text v-if="itemInfo" class="pt-4 pb-2 px-5">

          <!-- ── E-book no Pedido ──────────────────────────────────────── -->
          <div class="info-section-label">E-book no Pedido</div>

          <div class="info-row">
            <div class="info-row-left">
              <v-icon size="16" :color="itemInfo.flagebook === 'T' ? '#023449' : 'error'" class="mr-2">
                {{ itemInfo.flagebook === 'T' ? 'mdi-book-open-outline' : 'mdi-book-remove-outline' }}
              </v-icon>
              <span>E-book no pedido</span>
            </div>
            <v-chip x-small :color="itemInfo.flagebook === 'T' ? '#023449' : 'error'" dark style="font-weight:600;">
              {{ itemInfo.flagebook === 'T' ? 'Incluso' : 'Removido' }}
            </v-chip>
          </div>
          <div v-if="itemInfo.dtinclusaoebook" class="info-ts">
            <v-icon x-small color="grey lighten-1" class="mr-1">mdi-clock-outline</v-icon>
            Incluído em {{ itemInfo.dtinclusaoebook }}
          </div>
          <div v-if="itemInfo.flagebook === 'F' && itemInfo.dtremocaoebook" class="info-ts">
            <v-icon x-small color="error" class="mr-1">mdi-clock-outline</v-icon>
            Removido em {{ itemInfo.dtremocaoebook }}
          </div>
          <div v-if="itemInfo.flagebook === 'F' && itemInfo.motivoexclusao" class="info-obs">
            <v-icon x-small color="grey" class="mr-1">mdi-comment-text-outline</v-icon>
            {{ itemInfo.motivoexclusao }}
          </div>

          <div class="info-divider" />

          <!-- ── Pedido ────────────────────────────────────────────────── -->
          <div class="info-section-label">Pedido</div>

          <div class="info-row">
            <div class="info-row-left">
              <v-icon size="16" :color="itemInfo.flagefetivado === 'T' ? 'success' : '#f57c00'" class="mr-2">
                {{ itemInfo.flagefetivado === 'T' ? 'mdi-check-circle-outline' : 'mdi-clock-outline' }}
              </v-icon>
              <span>Efetivado no caixa</span>
            </div>
            <v-chip x-small :color="itemInfo.flagefetivado === 'T' ? 'success' : 'warning darken-1'" dark style="font-weight:600;">
              {{ itemInfo.flagefetivado === 'T' ? 'Sim' : 'Pendente' }}
            </v-chip>
          </div>
          <div v-if="itemInfo.dtefetivacao" class="info-ts">
            <v-icon x-small color="success" class="mr-1">mdi-clock-outline</v-icon>
            {{ itemInfo.dtefetivacao }}
          </div>

          <div class="info-divider" />

          <!-- ── WoliHub ────────────────────────────────────────────────── -->
          <div class="info-section-label">WoliHub</div>

          <div class="info-row">
            <div class="info-row-left">
              <v-icon size="16" :color="itemInfo.flagenviado === 'T' ? 'blue darken-1' : '#b0bec5'" class="mr-2">
                {{ itemInfo.flagenviado === 'T' ? 'mdi-send-check-outline' : 'mdi-send-outline' }}
              </v-icon>
              <span>Enviado para WoliHub</span>
            </div>
            <v-chip x-small :color="itemInfo.flagenviado === 'T' ? 'blue darken-1' : 'grey lighten-1'" :dark="itemInfo.flagenviado === 'T'" style="font-weight:600;">
              {{ itemInfo.flagenviado === 'T' ? 'Sim' : 'Não' }}
            </v-chip>
          </div>
          <div v-if="itemInfo.dtenvio" class="info-ts">
            <v-icon x-small color="blue darken-1" class="mr-1">mdi-clock-outline</v-icon>
            {{ itemInfo.dtenvio }}
          </div>

          <div class="info-row">
            <div class="info-row-left">
              <v-icon size="16" :color="itemInfo.flagdevolvido === 'T' ? 'deep-purple lighten-1' : '#b0bec5'" class="mr-2">
                mdi-arrow-u-left-bottom
              </v-icon>
              <span>E-book devolvido</span>
            </div>
            <v-chip x-small :color="itemInfo.flagdevolvido === 'T' ? 'deep-purple lighten-1' : 'grey lighten-1'" :dark="itemInfo.flagdevolvido === 'T'" style="font-weight:600;">
              {{ itemInfo.flagdevolvido === 'T' ? 'Sim' : 'Não' }}
            </v-chip>
          </div>
          <div v-if="itemInfo.dtdevolucao" class="info-ts">
            <v-icon x-small color="deep-purple lighten-1" class="mr-1">mdi-clock-outline</v-icon>
            {{ itemInfo.dtdevolucao }}
          </div>

          <div class="info-row">
            <div class="info-row-left">
              <v-icon size="16" :color="itemInfo.flagautorizado === 'T' ? 'teal' : '#b0bec5'" class="mr-2">
                {{ itemInfo.flagautorizado === 'T' ? 'mdi-shield-check-outline' : 'mdi-shield-outline' }}
              </v-icon>
              <span>Devolução autorizada</span>
            </div>
            <v-chip x-small :color="itemInfo.flagautorizado === 'T' ? 'teal' : 'grey lighten-1'" :dark="itemInfo.flagautorizado === 'T'" style="font-weight:600;">
              {{ itemInfo.flagautorizado === 'T' ? 'Sim' : 'Não' }}
            </v-chip>
          </div>
          <div v-if="itemInfo.dtautorizacao" class="info-ts">
            <v-icon x-small color="teal" class="mr-1">mdi-clock-outline</v-icon>
            {{ itemInfo.dtautorizacao }}
          </div>
          <div v-if="itemInfo.obsautorizacao" class="info-obs">
            <v-icon x-small color="teal" class="mr-1">mdi-comment-text-outline</v-icon>
            {{ itemInfo.obsautorizacao }}
          </div>

        </v-card-text>

        <v-card-actions class="px-5 pb-4">
          <v-spacer />
          <v-btn text color="primary" @click="dialogInfo = false">Fechar</v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import mixinFilial from '@/util/mixinFilial'
import SeletorFiliais  from '@/Components/UI/Inputs/SeletorFiliais.vue'
import SeletorPeriodo  from '@/Components/UI/Inputs/SeletorPeriodo.vue'
import SeletorCliente  from '@/Components/UI/Inputs/SeletorCliente.vue'
import DialogConfirmaRemocao    from './Widgets/DialogConfirmaRemocao.vue'
import DialogAutorizaDevolucao  from './Widgets/DialogAutorizaDevolucao.vue'
import DialogExcecaoClientes    from './Widgets/DialogExcecaoClientes.vue'
import PedidoProdutosExpandido  from './Widgets/PedidoProdutosExpandido.vue'
import PedidoCardMobile         from './Widgets/PedidoCardMobile.vue'

export default {
  name: 'WolihubPedidos',

  mixins: [mixinFilial],

  components: {
    SeletorFiliais,
    SeletorPeriodo,
    SeletorCliente,
    DialogConfirmaRemocao,
    DialogAutorizaDevolucao,
    DialogExcecaoClientes,
    PedidoProdutosExpandido,
    PedidoCardMobile,
  },

  data: () => ({
    carregando:        false,
    salvando:          false,
    filtrosVisiveis:   true,
    dialogExcecao:     false,
    pedidos:           [],
    dialogRemocao:     false,
    dialogAutorizacao: false,
    dialogInfo:        false,
    itemSelecionado:   null,
    itemInfo:          null,
    alertaFilial:      false,
    ultimaBusca:       null,
    filtroStatus:      null,
    filtroCombo:       null,
    paginaAtual:       1,
    itensPorPagina:    50,

    combosOpcoes: [
      { label: 'Total',    value: null,       hex: '#023449' },
      { label: 'VIP',      value: 'VIP',      hex: '#1976d2' },
      { label: 'Bronze',   value: 'BRONZE',   hex: '#795548' },
      { label: 'Prata',    value: 'PRATA',    hex: '#607d8b' },
      { label: 'Ouro',     value: 'OURO',     hex: '#ff8f00' },
      { label: 'Diamante', value: 'DIAMANTE', hex: '#00838f' },
    ],
    expandidos:        {},
    produtosPedido:    {},
    carregandoProdutos:{},
    gerandoPDF:        {},

    statusFiltroOpcoes: [
      { label: 'Total',      value: null,        chave: 'todos',    hex: '#023449' },
      { label: 'Efetivados', value: 'ativo',     chave: 'ativo',    hex: '#16a34a' },
      { label: 'Na fila',    value: 'nafila',    chave: 'nafila',   hex: '#1565c0' },
      { label: 'Pendentes',  value: 'pendente',  chave: 'pendente', hex: '#f59e0b' },
      { label: 'Removidos',  value: 'removido',  chave: 'removido', hex: '#dc2626' },
      { label: 'Devolvidos', value: 'devolvido', chave: 'devolvido', hex: '#7c3aed' },
    ],

    busca: {
      idfiliais:   [],
      idorcamento: null,
      cliente:     null,
      periodo: {
        inicio: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`,
        fim:    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`,
      },
    },
  }),

  computed: {
    ...mapState(['backendUrl', 'usuario']),

    horaUltimaBusca() {
      if (!this.ultimaBusca) return ''
      const h = String(this.ultimaBusca.getHours()).padStart(2, '0')
      const m = String(this.ultimaBusca.getMinutes()).padStart(2, '0')
      const s = String(this.ultimaBusca.getSeconds()).padStart(2, '0')
      return `${h}:${m}:${s}`
    },

    totaisPedidos() {
      const lista = this.pedidosFiltrados
      return {
        qtdEbooks:   lista.filter(p => p.flagebook === 'T').reduce((s, p) => s + (Number(p.qtdebook)   || 0), 0),
        valorEbooks: lista.filter(p => p.flagebook === 'T').reduce((s, p) => s + (Number(p.valorebook) || 0), 0),
        valorTotal:  lista.reduce((s, p) => s + (Number(p.vlrtotal) || 0), 0),
      }
    },

    filiaisWolihub() {
      if (!this.filiais.lista) return []
      const excluir = [4, 6, 12, 13, 22, 25, 28, 29, 33, 34, 37, 40, 42, 43]
      return this.filiais.lista.filter(f => !excluir.includes(f.idfilial))
    },

    filialSelecionada() {
      return this.busca.idfiliais && this.busca.idfiliais.length > 0
    },

    periodoValido() {
      return !!(this.busca.periodo?.inicio && this.busca.periodo?.fim)
    },

    podeBuscar() {
      return this.filialSelecionada && this.periodoValido
    },

    contagens() {
      const p = this.pedidos
      return {
        todos:      p.length,
        pendente:   p.filter(i => i.flagefetivado === 'F' && i.flagebook !== 'F').length,
        nafila:     p.filter(i => i.flagefetivado === 'T' && i.flagenviado === 'F' && i.flagebook !== 'F').length,
        ativo:      p.filter(i => i.flagenviado   === 'T' && i.flagdevolvido !== 'T').length,
        removido:   p.filter(i => i.flagebook     === 'F').length,
        autorizado: p.filter(i => i.flagautorizado === 'T').length,
        devolvido:  p.filter(i => i.flagdevolvido  === 'T').length,
      }
    },

    pedidosFiltradosPorStatus() {
      const f = this.filtroStatus
      if (!f) return this.pedidos
      return this.pedidos.filter(p => {
        if (f === 'pendente')   return p.flagefetivado === 'F' && p.flagebook !== 'F'
        if (f === 'nafila')     return p.flagefetivado === 'T' && p.flagenviado === 'F' && p.flagebook !== 'F'
        if (f === 'ativo')      return p.flagenviado   === 'T' && p.flagdevolvido !== 'T'
        if (f === 'removido')   return p.flagebook     === 'F'
        if (f === 'autorizado') return p.flagautorizado === 'T'
        if (f === 'devolvido')  return p.flagdevolvido  === 'T'
        return true
      })
    },

    pedidosFiltrados() {
      if (!this.filtroCombo) return this.pedidosFiltradosPorStatus
      return this.pedidosFiltradosPorStatus.filter(p => p.combo === this.filtroCombo)
    },

    totalPaginas() {
      return Math.ceil(this.pedidosFiltrados.length / this.itensPorPagina)
    },

    pedidosPaginados() {
      const inicio = (this.paginaAtual - 1) * this.itensPorPagina
      return this.pedidosFiltrados.slice(inicio, inicio + this.itensPorPagina)
    },

    // Valores em R$ por status (vlrtotal)
    valoresStatus() {
      const p = this.pedidos
      const soma = lista => lista.reduce((s, i) => s + (Number(i.vlrtotal) || 0), 0)
      return {
        todos:    soma(p),
        pendente: soma(p.filter(i => i.flagefetivado === 'F' && i.flagebook !== 'F')),
        nafila:   soma(p.filter(i => i.flagefetivado === 'T' && i.flagenviado === 'F' && i.flagebook !== 'F')),
        ativo:    soma(p.filter(i => i.flagenviado   === 'T' && i.flagdevolvido !== 'T')),
        removido: soma(p.filter(i => i.flagebook     === 'F')),
        autorizado: soma(p.filter(i => i.flagautorizado === 'T')),
        devolvido:  soma(p.filter(i => i.flagdevolvido  === 'T')),
      }
    },

    // Valores em R$ por combo (valorebook)
    valoresCombo() {
      const base = this.pedidosFiltradosPorStatus
      const soma = lista => lista.reduce((s, i) => s + (Number(i.valorebook) || 0), 0)
      const result = { null: soma(base) }
      this.combosOpcoes.forEach(c => {
        if (c.value !== null) result[c.value] = soma(base.filter(p => p.combo === c.value))
      })
      return result
    },

    // Contagens de combo sobre a lista já filtrada por status
    contagensCombo() {
      const base = this.pedidosFiltradosPorStatus
      const result = { null: base.reduce((s, p) => s + (Number(p.qtdebook) || 0), 0) }
      this.combosOpcoes.forEach(c => {
        if (c.value !== null)
          result[c.value] = base.filter(p => p.combo === c.value).reduce((s, p) => s + (Number(p.qtdebook) || 0), 0)
      })
      return result
    },

  },

  watch: {
    'filiais.lista': {
      handler(lista) {
        if (lista && lista.length > 0 && this.busca.idfiliais.length === 0) {
          this.busca.idfiliais = [this.usuario.idfilial]
        }
      },
      immediate: true,
    },

    filtroStatus() { this.paginaAtual = 1; this.expandidos = {} },
    filtroCombo()  { this.paginaAtual = 1; this.expandidos = {} },
  },

  methods: {
    // ── Busca ──────────────────────────────────────────────────────────────
    buscar() {
      if (!this.filialSelecionada) {
        this.alertaFilial = true
        setTimeout(() => { this.alertaFilial = false }, 4000)
        return
      }
      this.alertaFilial       = false
      this.carregando         = true
      this.pedidos            = []
      this.filtroStatus       = null
      this.filtroCombo        = null
      this.paginaAtual        = 1
      this.expandidos         = {}
      this.produtosPedido     = {}
      this.carregandoProdutos = {}
      this.gerandoPDF         = {}

      axios
          .post(`${this.backendUrl}wolihub/pedidos/listar`, {
            idempresa:   this.busca.idfiliais[0],
            dtinicio:    this.busca.periodo.inicio,
            dtfim:       this.busca.periodo.fim,
            idorcamento: this.busca.idorcamento ? parseInt(this.busca.idorcamento) : null,
            idclifor:    this.busca.cliente?.idcliente || null,
          })
          .then(res => {
            this.pedidos = res.data || []
            this.ultimaBusca = new Date()
          })
          .catch(() => { this.$root.$snackbar.showError('Erro ao buscar pedidos.') })
          .finally(() => { this.carregando = false })
    },

    // ── Remoção ────────────────────────────────────────────────────────────
    abrirRemocao(item) {
      this.itemSelecionado = item
      this.dialogRemocao   = true
    },

    confirmarRemocao(motivo) {
      this.salvando = true
      axios
          .post(`${this.backendUrl}wolihub/ebook/remover`, {
            idempresa:   this.itemSelecionado.idempresa,
            idorcamento: this.itemSelecionado.idorcamento,
            idclifor:    this.itemSelecionado.idclifor,
            motivo:      motivo || null,
          })
          .then(() => {
            this.atualizarLinha(this.itemSelecionado, { flagebook: 'F', motivoexclusao: motivo || null })
            this.dialogRemocao = false
            this.$root.$snackbar.showSuccess('E-book removido com sucesso!')
          })
          .catch(() => { this.$root.$snackbar.showError('Erro ao remover o e-book do pedido.') })
          .finally(() => { this.salvando = false })
    },

    // ── Autorização ────────────────────────────────────────────────────────
    abrirAutorizacao(item) {
      this.itemSelecionado  = item
      this.dialogAutorizacao = true
    },

    confirmarAutorizacao(observacao) {
      this.salvando = true
      axios
          .post(`${this.backendUrl}wolihub/devolucao/autorizar`, {
            idempresa:   this.itemSelecionado.idempresa,
            idorcamento: this.itemSelecionado.idorcamento,
            observacao,
          })
          .then(() => {
            this.atualizarLinha(this.itemSelecionado, { flagautorizado: 'T', obsautorizacao: observacao })
            this.dialogAutorizacao = false
            this.$root.$snackbar.showSuccess('Devolução autorizada com sucesso!')
          })
          .catch(() => { this.$root.$snackbar.showError('Erro ao autorizar a devolução.') })
          .finally(() => { this.salvando = false })
    },

    // ── Info ───────────────────────────────────────────────────────────────
    abrirInfo(item) {
      this.itemInfo  = item
      this.dialogInfo = true
    },

    // ── Helpers ────────────────────────────────────────────────────────────
    atualizarLinha(item, campos) {
      const idx = this.pedidos.findIndex(
          p => p.idorcamento === item.idorcamento && p.idempresa === item.idempresa
      )
      if (idx !== -1) {
        this.$set(this.pedidos, idx, { ...this.pedidos[idx], ...campos })
      }
    },

    podeAutorizar(item) {
      return (
          item.flagenviado    === 'T' &&
          item.flagautorizado === 'F' &&
          item.flagdevolvido  === 'F' &&
          this.permissao('wolihub_autorizar_devolucao')
      )
    },

    tooltipRemover(item) {
      if (item.flagebook    === 'F') return 'E-book já removido do pedido'
      if (item.flagefetivado === 'T') return 'Pedido efetivado — não é possível remover'
      return 'Remover E-book'
    },

    tooltipAutorizar(item) {
      if (item.flagautorizado === 'T') {
        return item.obsautorizacao ? `Autorizado: ${item.obsautorizacao}` : 'Devolução já autorizada'
      }
      if (item.flagdevolvido === 'T') return 'E-book já foi devolvido'
      if (item.flagenviado   === 'F') return 'Aguardando envio para WoliHub'
      return 'Autorizar Devolução'
    },

    // ── Expansão de linha ──────────────────────────────────────────────────
    toggleExpandir(item) {
      const key    = `${item.idempresa}_${item.idorcamento}`
      const aberto = !!this.expandidos[key]

      // fecha todos os outros antes de abrir
      this.expandidos = {}

      if (!aberto) {
        this.$set(this.expandidos, key, true)
        if (!this.produtosPedido[key]) {
          this.carregarProdutos(item)
        }
      }
    },

    carregarProdutos(item) {
      const key = `${item.idempresa}_${item.idorcamento}`
      this.$set(this.carregandoProdutos, key, true)
      axios
          .post(`${this.backendUrl}wolihub/pedido/produtos`, {
            idempresa:   item.idempresa,
            idorcamento: item.idorcamento,
          })
          .then(res => { this.$set(this.produtosPedido, key, res.data || []) })
          .catch(() => {
            this.$root.$snackbar.showError('Erro ao carregar produtos do pedido.')
            this.$set(this.expandidos, key, false)
          })
          .finally(() => { this.$set(this.carregandoProdutos, key, false) })
    },

    estaExpandido(item) {
      return !!this.expandidos[`${item.idempresa}_${item.idorcamento}`]
    },

    produtosDoItem(item) {
      return this.produtosPedido[`${item.idempresa}_${item.idorcamento}`] || []
    },

    estaCarregandoProdutos(item) {
      return !!this.carregandoProdutos[`${item.idempresa}_${item.idorcamento}`]
    },

    statusPedido(item) {
      if (item.flagebook     === 'F') return { label: 'Removido', color: 'error',                icon: 'mdi-book-remove-outline'  }
      if (item.flagdevolvido === 'T') return { label: 'Devolvido', color: 'deep-purple lighten-1', icon: 'mdi-arrow-u-left-bottom' }
      if (item.flagenviado   === 'T') return { label: 'Efetivado', color: 'success',             icon: 'mdi-check-circle-outline' }
      if (item.flagefetivado === 'T') return { label: 'Na fila',  color: 'blue darken-1',        icon: 'mdi-clock-fast'           }
      return                                 { label: 'Pendente', color: 'warning darken-1',     icon: 'mdi-clock-outline'        }
    },

    totaisProdutos(item) {
      return this.produtosDoItem(item).reduce((acc, p) => ({
        descontoEbook:      (acc.descontoEbook      || 0) + (p.descontoebook          || 0),
        descDevolvido:      (acc.descDevolvido      || 0) + (p.valordevolucaoproduto  || 0),
        creditoAvulso:      (acc.creditoAvulso      || 0) + (p.valorcreditoavulso     || 0),
        vlrTotal:           (acc.vlrTotal           || 0) + (p.vlrtotal               || 0),
      }), {})
    },
    temDevolucao(item) {
      return this.produtosDoItem(item).some(p => p.qtddevolvida || p.valorcreditoavulso || p.valordevolucaoproduto)
    },

    parseDtPedido(dt) {
      if (!dt) return { data: '-', hora: '' }
      // suporta "DD/MM/YYYY HH:MM:SS" e "YYYY-MM-DDTHH:MM:SS"
      const partes = dt.includes('T') ? dt.split('T') : dt.split(' ')
      let data = partes[0] || ''
      const hora = partes[1] ? partes[1].substring(0, 5) : ''
      // normaliza YYYY-MM-DD → DD/MM/YYYY
      if (data.includes('-')) {
        data = data.split('-').reverse().join('/')
      }
      return { data, hora }
    },

    _pdfBase(item, corHeader, titulo = 'Comprovante de Benefício WoliHub') {
      const { jsPDF } = require('jspdf')
      require('jspdf-autotable')

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const W = doc.internal.pageSize.getWidth()
      const marginX = 14
      const { data, hora } = this.parseDtPedido(item.dtpedido)

      // Cabeçalho
      const LOGO = 'data:image/png;base64,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'

      doc.setFillColor(...corHeader)
      doc.rect(0, 0, W, 28, 'F')

      // Logo no canto esquerdo do cabeçalho
      const logoSize = 14
      const logoPad = 1.5
      try {
        const headerH = 28
        const logoBlockH = logoSize + logoPad * 2
        const logoY = (headerH - logoBlockH) / 2
        doc.setFillColor(255, 255, 255)
        doc.roundedRect(marginX - logoPad, logoY, logoSize + logoPad * 2, logoBlockH, 2, 2, 'F')
        doc.addImage(LOGO, 'PNG', marginX, logoY + logoPad, logoSize, logoSize)
      } catch (e) { /* sem logo */ }

      // Textos do cabeçalho centralizados verticalmente (headerH = 28mm, centro = 14mm)
      const textX = marginX + logoSize + logoPad * 2 + 4
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.text(titulo, textX, 14)
      doc.setFontSize(7.5)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(200, 225, 235)
      doc.text('Documento gerado para esclarecimento de valores ao cliente', textX, 18)
      doc.setFontSize(7)
      doc.setTextColor(180, 210, 225)
      doc.text(`Emitido em: ${new Date().toLocaleString('pt-BR')}`, W - marginX, 25, { align: 'right' })

      // Dados do pedido
      let y = 36
      doc.setTextColor(30, 58, 74)
      doc.setFontSize(8.5)
      doc.setFont('helvetica', 'bold')
      doc.text('DADOS DO PEDIDO', marginX, y)
      doc.setDrawColor(...corHeader)
      doc.setLineWidth(0.4)
      doc.line(marginX, y + 1.5, W - marginX, y + 1.5)

      y += 7
      const campo = (label, valor, x, yPos) => {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(7.5)
        doc.setTextColor(100, 130, 150)
        doc.text(label, x, yPos)
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(30, 58, 74)
        doc.text(String(valor || '—'), x, yPos + 4.5)
      }

      campo('Nº do Pedido', `#${item.idorcamento}`, marginX, y)
      campo('Data', `${data} ${hora}`, W / 2, y)
      y += 10
      campo('Cliente', item.nomecliente || '—', marginX, y)
      if (item.combo) { campo('Benefício Adquirido', '', W / 2, y) }
      y += 10

      // Badge visual do combo
      if (item.combo) {
        const badgePadX = 3
        const badgeTextX = W / 2 + badgePadX
        const badgeTextY = y - 5   // alinhado à linha de valor do campo
        const badgeText = item.combo
        doc.setFontSize(8.5)
        doc.setFont('helvetica', 'bold')
        const textW = doc.getTextWidth(badgeText)
        doc.setFillColor(2, 64, 89)
        doc.roundedRect(W / 2, badgeTextY - 3.5, textW + badgePadX * 2, 5, 2, 2, 'F')
        doc.setTextColor(255, 255, 255)
        doc.text(badgeText, badgeTextX, badgeTextY - 0.1)
        doc.setTextColor(30, 58, 74)
      }

      return { doc, W, marginX, y }
    },

    _abrirPDF(doc, titulo) {
      const blob = doc.output('blob')
      const url  = URL.createObjectURL(blob)
      const win  = window.open('', '_blank')
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title></head><body style="margin:0;height:100vh;"><embed src="${url}" type="application/pdf" width="100%" height="100%"></body></html>`)
      win.document.close()
    },

    _pdfRodape(doc, W, marginX) {
      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        const pageH = doc.internal.pageSize.getHeight()
        doc.setFillColor(245, 249, 252)
        doc.rect(0, pageH - 10, W, 10, 'F')
        doc.setFontSize(7)
        doc.setTextColor(150, 170, 185)
        doc.setFont('helvetica', 'normal')
        doc.text('Documento gerado pelo sistema Gestor · Comercial Ivaipora', marginX, pageH - 4)
        doc.text(`Página ${i} de ${pageCount}`, W - marginX, pageH - 4, { align: 'right' })
      }
    },

    iniciarPDFVenda(item) {
      const key = `${item.idempresa}_${item.idorcamento}`
      if (this.produtosPedido[key]) {
        this.gerarPDFVenda(item)
        return
      }
      this.$set(this.gerandoPDF, key, true)
      axios
          .post(`${this.backendUrl}wolihub/pedido/produtos`, {
            idempresa:   item.idempresa,
            idorcamento: item.idorcamento,
          })
          .then(res => {
            this.$set(this.produtosPedido, key, res.data || [])
            this.gerarPDFVenda(item)
          })
          .catch(() => {
            this.$root.$snackbar.showError('Erro ao carregar produtos do pedido.')
          })
          .finally(() => { this.$set(this.gerandoPDF, key, false) })
    },

    gerarPDFVenda(item) {
      const moeda = v => parseFloat(v || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
      const cor = [2, 64, 89]
      const { doc, W, marginX, y: yBase } = this._pdfBase(item, cor)
      const produtos = this.produtosDoItem(item)
      const totais = this.totaisProdutos(item)
      let y = yBase + 4

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8.5)
      doc.setTextColor(...cor)
      doc.text('Detalhes da Venda', marginX, y)
      doc.setDrawColor(...cor)
      doc.line(marginX, y + 1.5, W - marginX, y + 1.5)
      y += 4

      const lineH = 8 * 1.15 * 0.3527 // altura de linha em mm (fontSize 8)
      const col2Data = [] // dados estruturados por linha para desenhar label/valor

      const body = produtos.map((p) => {
        const isEbook = p.idproduto === 248545
        const nome = isEbook
            ? `${p.produto} [E-book · ${this.comboDoEbook(p.vlrtotal).nome}]`
            : `${p.produto}${p.idsubproduto ? ' (Cód.: ' + p.idsubproduto + ')' : ''}`

        const structured = []
        if (!isEbook && p.vlrtotalbruto !== p.vlrtotal)
          structured.push({ label: 'Valor Original', value: moeda(p.vlrtotalbruto), type: 'normal' })
        if (p.desconto)
          structured.push({ label: 'Desconto Comercial', value: `-${moeda(p.desconto)}`, type: 'discount' })
        if (p.descontoebook)
          structured.push({ label: `Beneficio E-book (${Number(p.percdescontoebook).toFixed(2)}%)`, value: `-${moeda(p.descontoebook)}`, type: 'discount' })

        const hasSep = p.desconto || p.descontoebook || (!isEbook && p.vlrtotalbruto !== p.vlrtotal)
        if (hasSep) {
          structured.push({ type: 'sep' })
          structured.push({ type: 'empty' })
        }
        structured.push({
          label: isEbook ? 'Benefício Incluso' : 'Valor Final',
          value: moeda(p.vlrtotal),
          type: 'total'
        })

        col2Data.push(structured)

        // Texto placeholder para autoTable calcular a altura da célula
        // 'empty' é filtrado pois sep+empty juntos equivalem a uma só linha em branco
        const linhas = structured
            .filter(s => s.type !== 'empty')
            .map(s => s.type === 'sep' ? '' : `${s.label}  ${s.value}`)
        return [nome, p.quantidade, linhas.join('\n')]
      })

      doc.autoTable({
        startY: y,
        head: [['Produto', { content: 'Qtd.', styles: { halign: 'center' } }, 'Composição do Total']],
        body,
        theme: 'grid',
        headStyles:          { fillColor: cor, textColor: [255,255,255], fontSize: 8, fontStyle: 'bold', halign: 'left' },
        columnStyles:        { 0: { cellWidth: 'auto' }, 1: { cellWidth: 18, halign: 'center' }, 2: { cellWidth: 65, halign: 'right' } },
        bodyStyles:          { fontSize: 8, textColor: [30,58,74], cellPadding: 3 },
        alternateRowStyles:  { fillColor: [242,248,252] },
        footStyles:          { fillColor: cor, textColor: [255,255,255], fontStyle: 'bold', fontSize: 8.5 },
        foot:                [[{ content: '', colSpan: 2 }, { content: '', colSpan: 1 }]],
        margin:              { left: marginX, right: marginX },
        didDrawCell: (data) => {
          // Redesenha coluna 2 com label esquerda / valor direita
          if (data.section === 'body' && data.column.index === 2) {
            const structured = col2Data[data.row.index]
            if (!structured) return

            const { x, y: cy, width, height } = data.cell
            const pad = 3
            const bgColor = data.cell.styles.fillColor
            doc.setFillColor(...(Array.isArray(bgColor) ? bgColor : [255,255,255]))
            doc.rect(x + 0.3, cy + 0.3, width - 0.6, height - 0.6, 'F')
            doc.setCharSpace(0)
            doc.setFontSize(8)
            doc.setFont('helvetica', 'normal')

            let lineY = cy + pad + lineH * 0.8
            for (const line of structured) {
              if (line.type === 'sep') {
                const sepY = lineY - lineH * 0.3
                doc.setDrawColor(180, 200, 215)
                doc.setLineWidth(0.25)
                doc.setLineDashPattern([1, 1.2], 0)
                doc.line(x + pad, sepY, x + width - pad, sepY)
                doc.setLineDashPattern([], 0)
                lineY += lineH * 0.5
                continue
              }
              if (line.type === 'empty') { lineY += lineH * 0.5; continue }

              const isTotal   = line.type === 'total'
              const isDisc    = line.type === 'discount'
              doc.setFont('helvetica', isTotal ? 'bold' : 'normal')
              doc.setFontSize(8)
              doc.setTextColor(isDisc ? 120 : isTotal ? 30 : 30, isDisc ? 140 : isTotal ? 58 : 58, isDisc ? 160 : isTotal ? 74 : 74)
              doc.text(line.label, x + pad, lineY)
              doc.setFont('helvetica', isTotal ? 'bold' : 'normal')
              doc.setTextColor(30, 58, 74)
              doc.text(line.value, x + width - pad, lineY, { align: 'right' })
              lineY += lineH
            }
          }
          if (data.section !== 'foot') return
          if (data.column.index === 2) {
            const { x, y: cy, width, height } = data.cell
            doc.setFillColor(...cor)
            doc.rect(x, cy, width, height, 'F')
            const midY = cy + height * 0.65
            doc.setFont('helvetica', 'normal')
            doc.setFontSize(8)
            doc.setTextColor(200, 225, 235)
            doc.text('Valor Final do Pedido', x + 3, midY)
            doc.setFont('helvetica', 'bold')
            doc.setFontSize(9.5)
            doc.setTextColor(255, 255, 255)
            doc.text(moeda(totais.vlrTotal), x + width - 3, midY, { align: 'right' })
          }
        },
      })

      this._pdfRodape(doc, W, marginX)
      this._abrirPDF(doc, `Comprovante de Venda - Pedido #${item.idorcamento}`)
    },

    percFormatado(val) {
      if (!val && val !== 0) return ''
      const n = parseFloat(val)
      return isNaN(n) ? '' : `${n.toFixed(1)}%`
    },

    corCombo(combo) {
      const cores = {
        'VIP':      'blue darken-1',
        'BRONZE':   'brown',
        'PRATA':    'blue-grey',
        'OURO':     'amber darken-2',
        'DIAMANTE': 'cyan darken-2',
      }
      return cores[combo] || 'primary'
    },

    comboDoEbook(vlrtotal) {
      const combos = [
        { nome: 'VIP',      valor: 50,  hex: '#1976d2' },
        { nome: 'BRONZE',   valor: 100, hex: '#795548' },
        { nome: 'PRATA',    valor: 150, hex: '#607d8b' },
        { nome: 'OURO',     valor: 200, hex: '#ff8f00' },
        { nome: 'DIAMANTE', valor: 275, hex: '#00838f' },
      ]
      const v = Math.abs(Number(vlrtotal))
      // encontra o combo pelo valor exato; se não bater, pega o mais próximo
      const exato = combos.find(c => c.valor === v)
      if (exato) return exato
      return combos.reduce((prev, curr) =>
          Math.abs(curr.valor - v) < Math.abs(prev.valor - v) ? curr : prev
      )
    },
  },
}
</script>

<style scoped>
/* ── Shimmer ───────────────────────────────────────────────────────────── */
@keyframes shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}
.sk {
  display: block; border-radius: 4px;
  background: linear-gradient(90deg, #dde6ee 25%, #edf3f8 50%, #dde6ee 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
}
.sk-text { height: 13px; }
.sk-chip { height: 20px; border-radius: 10px; }

/* ── Barra de filtros (clara, abaixo do header) ───────────────────────── */
.filtro-toolbar {
  background: linear-gradient(180deg, #edf3f8 0%, #f5f8fb 100%);
  border-top: 1px solid rgba(2, 52, 73, 0.08);
  border-bottom: 2px solid #dce8f0;
  padding: 10px 16px;
  display: flex;
  align-items: stretch;
  gap: 12px;
  flex-wrap: wrap;
}

.filtro-toolbar-sep {
  width: 1px;
  background: #cddbe6;
  flex-shrink: 0;
  align-self: stretch;
}

.seg-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.seg-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-height: 20px;
  overflow: hidden;
}

.seg-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #5a7a8e;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}

.seg-dep-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  color: var(--dep-cor);
  background: color-mix(in srgb, var(--dep-cor) 10%, #fff);
  border: 1px solid transparent;
  border-radius: 5px;
  padding: 2px 7px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}
.seg-dep-badge--visivel {
  opacity: 1;
  border-color: color-mix(in srgb, var(--dep-cor) 25%, transparent);
  pointer-events: auto;
}

.seg-group--filtrado .seg-track {
  outline: 1.5px solid color-mix(in srgb, var(--dep-cor, #94a3b8) 30%, transparent);
}

.seg-track {
  display: flex;
  flex: 1;
  background: #dce8f0;
  border-radius: 7px;
  padding: 3px;
  gap: 2px;
  box-shadow: inset 0 1px 2px rgba(2, 52, 73, 0.08);
}

.seg-btn {
  flex: 1;
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 6px 8px;
  border-radius: 5px;
  border: none;
  background: transparent;
  cursor: pointer;
  outline: none;
  font-family: inherit;
  transition: background 0.15s ease, box-shadow 0.15s ease;
  user-select: none;
}
.seg-btn:hover {
  background: rgba(255,255,255,0.6);
}
.seg-btn--ativo {
  background: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  outline: 1.5px solid var(--seg-cor);
}

.seg-btn-top {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 4px;
}

.seg-btn-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  line-height: 1;
}
.seg-btn--ativo .seg-btn-label {
  color: var(--seg-cor);
  font-weight: 700;
}

.seg-btn-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 19px;
  height: 17px;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(0,0,0,0.06);
  color: #64748b;
  line-height: 1;
}
.seg-btn--ativo .seg-btn-count {
  background: var(--seg-cor);
  color: #fff;
}

.seg-btn-div {
  width: 100%;
  height: 1px;
  background: #e2e8f0;
  margin-bottom: 4px;
  border-radius: 1px;
}
.seg-btn--ativo .seg-btn-div {
  background: color-mix(in srgb, var(--seg-cor) 20%, #e2e8f0);
}

.seg-btn-valor {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1;
  text-align: center;
}
.seg-btn--ativo .seg-btn-valor {
  color: var(--seg-cor);
}

/* ── Responsividade dos filtros ───────────────────────────────────────── */
@media (max-width: 1320px) {
  /* Grupos empilhados verticalmente, cada um ocupando uma linha */
  .filtro-toolbar {
    flex-direction: column;
    gap: 8px;
    padding: 8px 12px;
  }
  .filtro-toolbar-sep {
    width: 100%;
    height: 1px;
    align-self: auto;
  }

  /* Botões quebram para a próxima linha ao invés de transbordar */
  .seg-track { flex-wrap: wrap; }
  .seg-btn   { flex: 1; min-width: 80px; }
}

@media (max-width: 600px) {
  .seg-label { font-size: 10px; }
  /* 3 botões por linha — 2 linhas para 6 botões */
  .seg-btn        { flex: 1 0 calc(33.33% - 2px); min-width: 0; padding: 5px 6px; }
  .seg-btn-label  { font-size: 10px; }
  .seg-btn-valor  { font-size: 9px; }
  .seg-btn-count  { font-size: 9px; height: 13px; min-width: 15px; }
}

@media (max-width: 550px) {
  /* Cabeçalho: timestamp some */
  .table-header-timestamp { display: none; }

  /* Filtros compactos */
  .filtro-toolbar { padding: 6px 8px; gap: 5px; }

  .seg-btn        { padding: 4px; }
  .seg-btn-label  { font-size: 9px; }
  .seg-btn-valor  { font-size: 8px; }
  .seg-btn-count  { font-size: 8px; height: 12px; min-width: 13px; padding: 0 3px; }
  .seg-btn-top    { gap: 3px; margin-bottom: 3px; }
  .seg-btn-div    { margin-bottom: 3px; }
}

@media (max-width: 400px) {
  .seg-label span { display: none; }
  .seg-btn-label  { font-size: 8px; }
  .seg-btn-valor  { font-size: 7px; }
}

/* ── Cabeçalho da tabela ───────────────────────────────────────────────── */
.table-header {
  background: linear-gradient(135deg, #023449 0%, #035878 100%);
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.table-header-row {
  display: flex;
  align-items: center;
  width: 100%;
}
.table-header-row--top { justify-content: space-between; }
.table-header-row--bottom { gap: 10px; }

.table-header-left    { display: flex; align-items: center; gap: 10px; }
.table-header-actions { display: flex; align-items: center; gap: 8px; }

.th-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px 9px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.08);
  cursor: pointer;
  outline: none;
  font-family: inherit;
  transition: all 0.15s ease;
  user-select: none;
}
.th-toggle-btn:hover {
  background: rgba(255,255,255,0.14);
  border-color: rgba(255,255,255,0.25);
}
.th-toggle-btn--ativo {
  background: rgba(255,255,255,0.14);
  border-color: rgba(255,255,255,0.25);
}
.th-exc-label {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,0.8);
  white-space: nowrap;
  margin-left: 2px;
}
.table-header-icon {
  width: 28px; height: 28px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.table-header-title { font-size: 13px; font-weight: 700; color: white; }
.table-header-sub   { font-size: 10px; color: rgba(255,255,255,0.6); margin-top: 1px; }

.table-header-timestamp {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  padding: 3px 8px;
  white-space: nowrap;
}
.table-header-timestamp-label {
  font-size: 10px;
  color: rgba(255,255,255,0.7);
}
.table-header-timestamp-hora {
  font-size: 11px;
  font-weight: 700;
  color: #ffffff;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}

/* ── Campo filtro padrão (Nº Pedido) ────────────────────────────────── */
.filtro-field >>> .v-input__control { min-height: 44px !important; }
.filtro-field >>> fieldset {
  border-color: #cbd5e1;
  border-width: 2px;
  transition: all 0.2s ease;
}
.filtro-field >>> .v-input__slot {
  background: #f8fafc;
  border-radius: 10px;
  transition: all 0.2s ease;
}
.filtro-field >>> .v-input__slot:hover fieldset { border-color: #023449; }
.filtro-field >>> .v-input--is-focused fieldset {
  border-color: #023449 !important;
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba(2, 52, 73, 0.1);
}
.filtro-field >>> .v-input--is-focused .v-input__slot { background: #ffffff !important; }

/* ── Footer da tabela de pedidos ──────────────────────────────────────── */
.pedidos-tfoot-row {
  background: #f0f5f8;
  border-top: 2px solid #c4d8e6;
}

.pedidos-tfoot-td {
  padding: 8px 16px !important;
  vertical-align: middle;
}

.pedidos-tfoot-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.pedidos-tfoot-val {
  font-size: 12px;
  font-weight: 700;
  color: #023449;
  font-variant-numeric: tabular-nums;
}

.pedidos-tfoot-val--total {
  font-size: 13px;
  font-weight: 800;
  color: #023449;
}

.pedidos-tfoot-pagina {
  font-size: 10px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

/* ── Paginação ─────────────────────────────────────────────────────────── */
.pedidos-paginacao {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.pag-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  border: 1px solid #d0dde8;
  background: #f4f8fb;
  cursor: pointer;
  outline: none;
  transition: background 0.13s ease, border-color 0.13s ease;
  padding: 0;
}
.pag-btn:hover:not(:disabled) {
  background: #e2edf5;
  border-color: #b0c8da;
}
.pag-btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.mobile-paginacao {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 16px;
  border-top: 1px solid #e4eef5;
}

.mobile-paginacao-label {
  font-size: 12px;
  font-weight: 600;
  color: #5a7a8e;
  font-variant-numeric: tabular-nums;
  min-width: 48px;
  text-align: center;
}

.mobile-paginacao .pag-btn {
  width: 32px;
  height: 32px;
}

/* ── Tabela principal com larguras fixas ───────────────────────────────── */
.pedidos-table >>> table {
  table-layout: fixed;
  width: 100%;
}

/* ── Thead ─────────────────────────────────────────────────────────────── */
.thead-row {
  background: linear-gradient(180deg, #f0f5f8 0%, #e8eef3 100%);
  border-bottom: 2px solid #d4e2ea;
}
.thead-th {
  color: #3d5166 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding-top: 11px !important;
  padding-bottom: 11px !important;
}

/* ── Linhas ────────────────────────────────────────────────────────────── */
.data-row {
  transition: background-color 0.12s ease, box-shadow 0.12s ease;
  border-bottom: 1px solid #edf2f6;
  position: relative;
}
.data-row > td {
  padding-top: 11px !important;
  padding-bottom: 11px !important;
}
.data-row:hover {
  background-color: #f0f6fa !important;
  box-shadow: inset 3px 0 0 #023449;
}
.data-row:last-child { border-bottom: none; }
.data-row--inativo { opacity: 0.55; }

/* Coluna pedido */
.pedido-cell { white-space: nowrap; }

.pedido-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #023449;
  background: #ddeef7;
  border: 1px solid #c2dded;
  border-radius: 5px;
  padding: 2px 7px;
  letter-spacing: 0.2px;
  margin-bottom: 5px;
}

.pedido-data { display: flex; align-items: center; font-size: 10px; color: #7a9db5; margin-top: 0; }
.pedido-hora { display: flex; align-items: center; font-size: 10px; color: #a8bfce; margin-top: 2px; }
.id-sec      { font-size: 10px; color: #9aacbc; margin-left: 3px; }

/* ── Ações desktop ─────────────────────────────────────────────────────── */
.acoes-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: #f5f8fa;
  border-radius: 8px;
  padding: 3px 4px;
}

/* ── Dialog de info ────────────────────────────────────────────────────── */
.info-header {
  background: linear-gradient(135deg, #023449 0%, #035878 100%);
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.info-header-icon {
  width: 28px; height: 28px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.info-header-title { font-size: 14px; font-weight: 700; color: white; }
.info-header-sub   { font-size: 11px; color: rgba(255,255,255,0.6); margin-top: 1px; }

.info-section-label {
  font-size: 9px;
  font-weight: 700;
  color: #8fa8bc;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
  margin-top: 4px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f4f7;
}
.info-row:last-of-type { border-bottom: none; }
.info-row-left {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #3d5166;
}

.info-obs {
  font-size: 11px;
  color: #8fa8bc;
  padding: 4px 0 8px 24px;
  font-style: italic;
}

.info-ts {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #9aacbc;
  padding: 2px 0 6px 24px;
}

.info-divider {
  height: 1px;
  background: #edf2f6;
  margin: 10px 0;
}

.expanded-row > td { padding: 0 !important; }

/* ── Mobile skeleton — espelha a estrutura do PedidoCardMobile ─────────── */
.mobile-card {
  margin: 8px;
  border: 1px solid #dde6ee;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.mobile-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #f5f8fa;
  border-bottom: 1px solid #eef2f6;
  gap: 8px;
}
.mobile-card-body {
  padding: 10px 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

/* ── Mobile: estado vazio ─────────────────────────────────────────────── */
.mobile-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 16px;
  text-align: center;
}
</style>
